/**
 * @fileoverview Terms of Use dialog modal for landing page.
 * @module features/landing/components/TermsOfUseDialog
 */

import { ReactElement } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

interface TermsOfUseDialogProps {
  /** Trigger element that opens the dialog */
  children: React.ReactNode
}

/**
 * Terms of Use dialog modal component.
 * Displays terms and conditions in a scrollable modal.
 *
 * @component
 */
export function TermsOfUseDialog({ children }: TermsOfUseDialogProps): ReactElement {
  const { t } = useTranslation('features-landing')

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" showCloseButton={false}>
        <DialogHeader className="flex-row items-center justify-between flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {t('termsOfUse.title')}
          </DialogTitle>
          <DialogClose className="rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2 -mr-2 scrollbar-elegant">
          <div className="space-y-6 text-sm text-muted-foreground py-4">
            {/* Last updated */}
            <p className="text-xs text-muted-foreground/70">
              {t('termsOfUse.lastUpdated')}
            </p>

            {/* Section 1 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.general.title')}
              </h3>
              <p>{t('termsOfUse.sections.general.p1')}</p>
              <p>{t('termsOfUse.sections.general.p2')}</p>
              <p>{t('termsOfUse.sections.general.p3')}</p>
            </section>

            {/* Section 2 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.status.title')}
              </h3>
              <p>{t('termsOfUse.sections.status.description')}</p>
              <p>{t('termsOfUse.sections.status.means')}</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{t('termsOfUse.sections.status.items.features')}</li>
                <li>{t('termsOfUse.sections.status.items.data')}</li>
                <li>{t('termsOfUse.sections.status.items.availability')}</li>
              </ul>
              <p>{t('termsOfUse.sections.status.modification')}</p>
            </section>

            {/* Section 3 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.noOffer.title')}
              </h3>
              <p>{t('termsOfUse.sections.noOffer.p1')}</p>
              <p>{t('termsOfUse.sections.noOffer.p2')}</p>
            </section>

            {/* Section 4 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.noWarranties.title')}
              </h3>
              <p>{t('termsOfUse.sections.noWarranties.description')}</p>
              <p>{t('termsOfUse.sections.noWarranties.particular')}</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{t('termsOfUse.sections.noWarranties.items.errorFree')}</li>
                <li>{t('termsOfUse.sections.noWarranties.items.accurate')}</li>
                <li>{t('termsOfUse.sections.noWarranties.items.needs')}</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.liability.title')}
              </h3>
              <p>{t('termsOfUse.sections.liability.description')}</p>
              <p>{t('termsOfUse.sections.liability.includes')}</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>{t('termsOfUse.sections.liability.items.financial')}</li>
                <li>{t('termsOfUse.sections.liability.items.profits')}</li>
                <li>{t('termsOfUse.sections.liability.items.decisions')}</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.ip.title')}
              </h3>
              <p>{t('termsOfUse.sections.ip.p1')}</p>
              <p>{t('termsOfUse.sections.ip.p2')}</p>
            </section>

            {/* Section 7 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.thirdParty.title')}
              </h3>
              <p>{t('termsOfUse.sections.thirdParty.p1')}</p>
              <p>{t('termsOfUse.sections.thirdParty.p2')}</p>
            </section>

            {/* Section 8 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.personalData.title')}
              </h3>
              <p>{t('termsOfUse.sections.personalData.description')}</p>
            </section>

            {/* Section 9 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.changes.title')}
              </h3>
              <p>{t('termsOfUse.sections.changes.p1')}</p>
              <p>{t('termsOfUse.sections.changes.p2')}</p>
            </section>

            {/* Section 10 */}
            <section className="space-y-2">
              <h3 className="text-base font-medium text-foreground">
                {t('termsOfUse.sections.law.title')}
              </h3>
              <p>{t('termsOfUse.sections.law.description')}</p>
            </section>

            {/* Footer note */}
            <div className="pt-4 border-t border-border text-xs text-muted-foreground/70 italic">
              <p>{t('termsOfUse.footerNote.line1')}</p>
              <p>{t('termsOfUse.footerNote.line2')}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
